import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Input, Form, Alert, Card, Typography, Space } from 'antd';
import { UserOutlined, LockOutlined, UserAddOutlined } from '@ant-design/icons';
import { registerUser } from '../../services/api';
import { RegisterRequest } from '../../types';

const { Title, Text } = Typography;

interface RegisterForm {
  username: string;
  password: string;
  confirmPassword: string;
}

const Register: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const onFinish = async (values: RegisterForm) => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const registerData: RegisterRequest = {
        username: values.username,
        password: values.password,
      };

      const result = await registerUser(registerData);
      console.log('注册成功:', result);
      
      setSuccess(true);
      
      // 3秒后跳转到登录页
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      console.error('注册失败:', err);
      setError(err.message || '注册失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    }}>
      <Card style={{ width: 400, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
        <Space direction="vertical" size="large" style={{ width: '100%', textAlign: 'center' }}>
          <div>
            <Title level={2} style={{ marginBottom: 8 }}>
              <UserAddOutlined style={{ marginRight: 8, color: '#52c41a' }} />
              用户注册
            </Title>
            <Text type="secondary">培训费用标准化检查系统</Text>
          </div>

          {error && (
            <Alert
              message="注册失败"
              description={error}
              type="error"
              closable
              onClose={() => setError(null)}
            />
          )}

          {success && (
            <Alert
              message="注册成功"
              description="账户创建成功，3秒后自动跳转到登录页..."
              type="success"
            />
          )}

          <Form
            name="register"
            onFinish={onFinish}
            autoComplete="off"
            layout="vertical"
            size="large"
            disabled={success}
          >
            <Form.Item
              label="用户名"
              name="username"
              rules={[
                { required: true, message: '请输入用户名' },
                { min: 3, message: '用户名至少3个字符' },
                { max: 50, message: '用户名不能超过50个字符' },
                { 
                  pattern: /^[a-zA-Z0-9_\u4e00-\u9fa5]+$/, 
                  message: '用户名只能包含字母、数字、下划线和中文字符' 
                }
              ]}
            >
              <Input 
                prefix={<UserOutlined />} 
                placeholder="请输入用户名"
                autoComplete="username"
              />
            </Form.Item>

            <Form.Item
              label="密码"
              name="password"
              rules={[
                { required: true, message: '请输入密码' },
                { min: 6, message: '密码至少6个字符' },
                { max: 100, message: '密码不能超过100个字符' }
              ]}
            >
              <Input.Password 
                prefix={<LockOutlined />} 
                placeholder="请输入密码"
                autoComplete="new-password"
              />
            </Form.Item>

            <Form.Item
              label="确认密码"
              name="confirmPassword"
              dependencies={['password']}
              rules={[
                { required: true, message: '请确认密码' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('两次输入的密码不一致'));
                  },
                }),
              ]}
            >
              <Input.Password 
                prefix={<LockOutlined />} 
                placeholder="请再次输入密码"
                autoComplete="new-password"
              />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading}
                style={{ width: '100%', height: 40 }}
              >
                {loading ? '注册中...' : '注册'}
              </Button>
            </Form.Item>
          </Form>

          {!success && (
            <div style={{ textAlign: 'center' }}>
              <Text type="secondary">已有账户？</Text>
              <Link to="/login" style={{ marginLeft: 8 }}>
                立即登录
              </Link>
            </div>
          )}
        </Space>
      </Card>
    </div>
  );
};

export default Register;